import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import { Avatar, Spin, Input } from "antd";
import { Link } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { defaultSchema } from "rehype-sanitize";
import { isMobile } from "react-device-detect";

import MediaPlayer from "components/media-player/media-player";
import PropertyDocumentsModal from "components/property-documents-modal/property-documents-modal";
import { FileAttachmentThumbnail } from "components/file-attachment/file-attachment-thumbnail";
import { StreamingThoughts } from "components/streaming-thoughts/streaming-thoughts";
import { FileAttachmentInput } from "components/file-attachment-input/file-attachment-input";
import { PaperclipIcon } from "components/icons/paperclip-icon";
import MarkdownWithCursor from "./markdown-with-cursor";
import { processContentWithCitations, TextWithCitations } from "../citation-utils/citation-utils";
import ChatFilterDropdown from "components/chat-filter-dropdown/chat-filter-dropdown";
import type { ChatFilterItem } from "components/chat-filter-dropdown/chat-filter-dropdown";
import BriefMessage from "components/brief-message/brief-message";
import { parseBriefAnswer } from "utils/brief-answer";
import { stripReasoning } from "utils/chat-answer";

import { StartChatStreamMutationVariables } from "graphql/mutations/start-chat-stream.generated";
import { useGetInstalledProductsQuery } from "graphql/queries/get-installed-products-by-property-ids.generated";
import { useGetInputSuggestionsQuery } from "graphql/queries/get-input-suggestions.generated";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import {
	useAiChatStatus,
	adminAutoStartSentVar,
	isChatDiscardedOnNavigation
} from "state/reactive-vars/ai-chat-status-var";
import { useChatWindowFiltersStore } from "state/zustand-stores/chat-window-filters-store";
import { useBrand } from "context/BrandContext";

import usePropertyDocumentsModal from "hooks/usePropertyDocumentsModal";
import {
	AiChatMessage as ExtendedWebAiChatMessage,
	ServerMessagesInfo,
	useAiChatConversation
} from "hooks/useAiChatConversation";

import routeNames from "constants/route-names";
import { AiChatOrigin, AiChatType, BriefEmailFrequency, InputSuggestionType, MediaType } from "constants/field-values";

import AiChatFeedback from "components/ai-chat-feedback/ai-chat-feedback";

// Configure sanitization to allow safe HTML tags including media elements
const sanitizeSchema = {
	...defaultSchema,
	tagNames: [...(defaultSchema.tagNames || []), "video", "source", "img"],
	attributes: {
		...(defaultSchema.attributes || {}),
		video: ["controls", "width", "height", "src"],
		source: ["src", "type"],
		img: ["src", "alt", "width", "height"]
	}
};

// Label shown on the Brief button (kept clean per design)
const BRIEF_BUTTON_LABEL = "Show me today’s brief";

// Maximum length of the question used as the chat title before Doctrine generates one.
const STARTER_TITLE_MAX_LENGTH = 50;

// Builds the Brief request message with the current date and time in Austin, TX (Central Time),
// so the brief is anchored to "today" in the property's time zone.
const getBriefMessage = (): string => {
	const friendlyDateTime = new Intl.DateTimeFormat("en-US", {
		timeZone: "America/Chicago",
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
		timeZoneName: "short"
	}).format(new Date());

	return `${BRIEF_BUTTON_LABEL} — ${friendlyDateTime}`;
};

// Shared Apollo options for the read queries in this component: always read from the network.
const NETWORK_ONLY_QUERY_OPTIONS = {
	errorPolicy: "none",
	fetchPolicy: "network-only",
	nextFetchPolicy: "network-only",
	notifyOnNetworkStatusChange: true
} as const;

const CHAT_MESSAGES_SELECTOR = ".chat-messages-container";

// Shared empty array. An inline `[]` would be a new array on every render, so using it as a
// destructuring default or a ternary branch invalidates every useMemo/useCallback that uses it.
const EMPTY_ARRAY: never[] = [];

// Maps a MIME type to the MediaType enum the media viewer expects.
const getMediaType = (mimeType: string): MediaType =>
	mimeType.startsWith("image/")
		? MediaType.Image
		: mimeType.startsWith("video/")
			? MediaType.Video
			: mimeType.startsWith("audio/")
				? MediaType.Audio
				: MediaType.Document;

// Whether the chat scroll container is within `threshold`px of the bottom.
const isChatScrolledToBottom = (container: Element, threshold = 100): boolean =>
	container.scrollHeight - container.scrollTop <= container.clientHeight + threshold;

const CITATION_WRAPPED_TAGS = ["p", "li", "h1", "h2", "h3", "code"] as const;

// Builds the ReactMarkdown `components` map: wraps the tags that contain text with citation
// rendering and makes images clickable. Extracted to avoid repeating six almost identical wrappers.
const buildMarkdownComponents = (
	citationMap: ReturnType<typeof processContentWithCitations>["citationMap"],
	onImageClick: (src: string) => void
): Components => {
	const wrapWithCitations =
		(tag: string) =>
		({ children }: { children?: React.ReactNode }) =>
			React.createElement(tag, null, <TextWithCitations citationMap={citationMap}>{children}</TextWithCitations>);

	// Built as a plain record and cast at the end: indexing the Components union by tag name is too
	// complex for TypeScript.
	const components: Record<string, unknown> = {
		img: ({ src, alt, ...props }: any) => (
			<img src={src} alt={alt} {...props} style={{ cursor: "pointer" }} onClick={() => src && onImageClick(src)} />
		),
		a: ({ href, children, ...props }: any) => (
			<a href={href} target="_blank" rel="noreferrer" {...props}>
				{children}
			</a>
		)
	};

	for (const tag of CITATION_WRAPPED_TAGS) {
		components[tag] = wrapWithCitations(tag);
	}

	return components as Components;
};

// Returns where a saved chat was started from. `chat_origin` on the row is the source of truth;
// older rows without that column are classified by their selections.
const resolveChatOrigin = (aiChat: any): AiChatOrigin | undefined => {
	if (aiChat?.chat_origin === AiChatOrigin.PRIMARY || aiChat?.chat_origin === AiChatOrigin.SPECIALTY) {
		return aiChat.chat_origin;
	}

	const selections = aiChat?.ai_chat_user_selections;
	if (!selections?.length) {
		return undefined;
	}

	return selections.some((s: any) => s?.installed_product_id || s?.shop_product_id || s?.guide_id)
		? AiChatOrigin.SPECIALTY
		: AiChatOrigin.PRIMARY;
};

interface ChatWindowProps {
	isDashboard?: boolean;
	chatHeaderDescription?: string;
	chatType?: InputSuggestionType;
	withFilters?: boolean;
	chatTypeMode?: AiChatType;
	autoStartMessage?: string;
	onBack?: () => void;
}

const ChatWindow = ({
	isDashboard,
	chatHeaderDescription = "For the best support, choose your properties or devices below.",
	chatType = InputSuggestionType.AI_SUGGESTED_QUERY,
	withFilters = true,
	chatTypeMode = AiChatType.CLIENT,
	onBack,
	autoStartMessage
}: ChatWindowProps) => {
	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();
	const { selectedProperty, propertyDocumentsModalOpen, openModalWithProperty, setPropertyDocumentsModalOpen } =
		usePropertyDocumentsModal();
	const [currentThreadId, setCurrentThreadId] = useState<string>("");
	const [newPress, setNewPress] = useState<boolean>(false);

	const selectedPropertiesFilter = withFilters
		? useChatWindowFiltersStore(state => state.filters.selectedPropertiesFilter)
		: EMPTY_ARRAY;
	const setPropertyFilter = useChatWindowFiltersStore(state => state.setPropertyFilters);

	const selectedProductsFilter = withFilters
		? useChatWindowFiltersStore(state => state.filters.selectedProductsFilter)
		: EMPTY_ARRAY;
	const setProductsFilters = useChatWindowFiltersStore(state => state.setProductsFilters);

	const [negativeFeedbackActive, setNegativeFeedbackActive] = useState<boolean>(false);
	const [fileViewer, setFileViewer] = useState<{
		type: MediaType | null;
		src: string;
	}>({ type: null, src: "" });

	/*
	Returns the chat row fields that must be updated with what the server just sent. They are returned
	instead of written here so the conversation hook can apply them in the same update as the messages:
	a second write would re-render the window between the two halves of one answer.
	*/
	const handleOnServerMessages = useCallback(
		({ aiChat, threadId, source }: ServerMessagesInfo) => {
			setCurrentThreadId(threadId);
			setNewPress(false);

			// The Intelligence chat has no filter dropdowns. Without this check it would overwrite the
			// client chat's property filter with the selections of its own chat.
			const userSelections = aiChat?.ai_chat_user_selections;

			if (source === "load" && withFilters && !isDashboard && userSelections?.length) {
				setPropertyFilter(
					userSelections.filter((element: any) => element.property_id).map((element: any) => element.property_id)
				);
			}

			const serverChatOrigin = resolveChatOrigin(aiChat);

			return {
				isBrief: aiChat?.is_brief || false,
				briefFrequency: aiChat?.brief_frequency || null,
				...(userSelections && { ai_chat_user_selections: userSelections }),
				// Restores the origin of a chat reopened from the history panel.
				...(serverChatOrigin && { chatOrigin: serverChatOrigin })
			};
		},
		[isDashboard, setPropertyFilter, withFilters]
	);

	// Which state slot this window uses: the client chat or the admin Intelligence chat.
	const { aiChatStatusValue, updateAiChatStatus } = useAiChatStatus(chatTypeMode);
	const {
		chatTitle = "",
		chatTopic = "",
		isBrief = false,
		briefFrequency = null,
		chatOrigin = AiChatOrigin.PRIMARY,
		installed_product_id = "",
		shop_product_id = "",
		guide_id = "",
		selected_properties_ids = EMPTY_ARRAY,
		selected_products_ids = EMPTY_ARRAY,
		selected_installed_products_ids = EMPTY_ARRAY,
		ai_chat_user_selections = EMPTY_ARRAY,
		historyOpen = false
	} = aiChatStatusValue;

	const {
		chatId,
		messages: chatMessages,
		isLoadingMessages: loadingChatMessages,
		loadError: chatLoadError,
		draft: newChatMessage,
		setDraft: setNewChatMessage,
		send,
		stop: handleStopStream,
		startNewThread: handleOnClickNewThread,
		isAiResponding,
		isLongRunning,
		streamId: currentStreamId,
		streamingText,
		streamingError,
		isStreamComplete,
		thoughts,
		isThinking,
		uploadingFiles,
		attachFiles,
		removeFile,
		isUploading,
		hasFiles
	} = useAiChatConversation({
		chatType: chatTypeMode,
		// A chat that is discarded on navigation does not keep its unsent draft.
		keepDraftOnUnmount: !isChatDiscardedOnNavigation(chatTypeMode, chatOrigin),
		onServerMessages: handleOnServerMessages
	});

	// Reasoning the model wrote into the answer does not count as the answer having started: the
	// thought headline stays up until real words arrive.
	const visibleStreamingText = useMemo(() => stripReasoning(streamingText), [streamingText]);

	const { theme } = useBrand();
	const submitIcon = newChatMessage
		? theme.aiChatSubmitIcon[1] || theme.aiChatSubmitIcon[0]
		: theme.aiChatSubmitIcon[0];

	const [showScrollToBottom, setShowScrollToBottom] = useState<boolean>(false);
	const [isScrolling, setIsScrolling] = useState<boolean>(false);

	const chatInputRef = useRef<HTMLInputElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	// Guard to initialize property filter only once on mount
	const isPropertiesInitialized = useRef(false);

	useEffect(() => {
		if (streamingText.length > 0) {
			const chatContainer = document.querySelector(CHAT_MESSAGES_SELECTOR);
			if (chatContainer) {
				const isAtBottom = isChatScrolledToBottom(chatContainer);

				if (!isScrolling && !isAtBottom) {
					setShowScrollToBottom(true);
				}
			}
		}
	}, [streamingText, isScrolling]);

	// Initialize property filter with all user properties so all appear selected by default.
	useEffect(() => {
		if (!isPropertiesInitialized.current && loggedUserValue?.user_properties?.length) {
			const allIds = loggedUserValue.user_properties.map(up => up.property_id);
			setPropertyFilter(allIds);
			isPropertiesInitialized.current = true;
		}
	}, [loggedUserValue.user_properties, setPropertyFilter]);

	const handleOnCompleteGetInstalledProducts = useCallback(
		(data: any) => {
			const { installed_products = [] } = data || {};
			if (!installed_products.length) return;

			const seen = new Set<string>();
			const allProductIds = installed_products.reduce((acc: string[], ip: any) => {
				if (ip.product && !seen.has(ip.product_id)) {
					seen.add(ip.product_id);
					acc.push(ip.product_id);
				}
				return acc;
			}, []);

			setProductsFilters(allProductIds);
		},
		[setProductsFilters]
	);

	const handleOnErrorGetInstalledProducts = useCallback((error: ApolloError) => {
		console.log("useGetInstalledProductsQuery Error: ", error);
	}, []);

	const { data: installedProductsData } = useGetInstalledProductsQuery({
		skip: !selectedPropertiesFilter.length,
		variables: {
			property_ids: selectedPropertiesFilter
		},
		...NETWORK_ONLY_QUERY_OPTIONS,
		onError: handleOnErrorGetInstalledProducts,
		onCompleted: handleOnCompleteGetInstalledProducts
	});

	const handleOnErrorsGetAiSuggestedQueries = useCallback((error: ApolloError) => {
		console.log("GetAiSuggestedQueries Error: ", error);
	}, []);

	const handleOnCompletedGetAiSuggestedQueries = useCallback((data: any) => {
		// console.log("Completed Get AiSuggestedQueries data: ", data);
	}, []);

	const { data: aiSuggestedQueriesData } = useGetInputSuggestionsQuery({
		variables: {
			type: chatType
		},
		...NETWORK_ONLY_QUERY_OPTIONS,
		onError: handleOnErrorsGetAiSuggestedQueries,
		onCompleted: handleOnCompletedGetAiSuggestedQueries
	});

	// File selection handler
	const handleFileSelect = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			await attachFiles(event.target.files);
			// Reset the input so the same file can be selected again
			if (event.target) {
				event.target.value = "";
			}
		},
		[attachFiles]
	);

	useEffect(() => {
		// Scroll to the bottom when messages change
		if (chatMessages && chatMessages.length > 0 && newPress) {
			const scrollToBottom = () => {
				const chatContainer = document.querySelector(CHAT_MESSAGES_SELECTOR);
				if (chatContainer) {
					chatContainer.scrollTop = chatContainer.scrollHeight;
				}
			};

			// Small delay to ensure the DOM has been updated
			setTimeout(scrollToBottom, 100);
		}
	}, [newPress]);

	/*
	Sends the question with the scope of this window: the header filters, or the exact device, product
	or guide a specialty chat was opened from, plus the Brief flags. The conversation hook knows
	nothing about any of that; it only sends what it receives.
	*/
	const handleOnPressSend = useCallback(
		(messageText?: string, options?: { isBrief?: boolean }) => {
			// Allow callers (e.g. the Brief button) to force is_brief without waiting for the
			// reactive var update to propagate into this callback's closure.
			const effectiveIsBrief = options?.isBrief ?? isBrief;

			if (options?.isBrief && process.env.REACT_APP_FF_BRIEF_ENABLED !== "true") {
				return;
			}

			const variables: Partial<StartChatStreamMutationVariables> = {
				is_brief: effectiveIsBrief,
				// "Show me today's brief" means the last 24 hours; the cron sends the user's own frequency.
				brief_frequency: effectiveIsBrief ? BriefEmailFrequency.DAILY : undefined,
				// Saved on the chat row so the history panel can restore it.
				chat_origin: chatOrigin
			};

			// A specialty chat (shop product, guide) carries the exact properties it is scoped to. Those
			// replace the header filters, which only apply to the general chat.
			if (selected_properties_ids.length) {
				variables.selected_properties_ids = [...selected_properties_ids];
			} else if (selectedPropertiesFilter.length > 0) {
				variables.selected_properties_ids = [...selectedPropertiesFilter];
			} else {
				variables.selected_properties_ids = loggedUserValue.user_properties.map(element => element.property_id);
			}

			if (selected_installed_products_ids && selected_installed_products_ids.length) {
				variables.selected_installed_products_ids = [...selected_installed_products_ids];
			} else if (selected_products_ids.length) {
				variables.selected_products_ids = [...selected_products_ids];
			} else {
				variables.selected_products_ids = [...selectedProductsFilter];
			}

			if (installed_product_id) {
				variables.installed_product_id = installed_product_id;
			}

			if (shop_product_id) {
				variables.shop_product_id = shop_product_id;
			}

			if (guide_id) {
				variables.guide_id = guide_id;

				variables.selected_products_ids = [...selected_products_ids];
			}

			// For new chats initiated from a specific device/product/guide context,
			// prepend the topic so the AI receives the full intent of the conversation
			if (!chatId && chatTopic) {
				variables.message = `${chatTopic}\n\n${messageText || newChatMessage}`;
			}

			const sent = send(messageText, {
				variables,
				status: {
					// Persist the brief flag so follow-ups and the chat UI stay in sync
					isBrief: effectiveIsBrief,
					briefFrequency: effectiveIsBrief ? BriefEmailFrequency.DAILY : null
				}
			});

			if (sent) {
				setNegativeFeedbackActive(false);
				setNewPress(true);
			}
		},
		[
			chatId,
			chatOrigin,
			chatTopic,
			guide_id,
			installed_product_id,
			isBrief,
			loggedUserValue.user_properties,
			newChatMessage,
			selectedPropertiesFilter,
			selectedProductsFilter,
			selected_installed_products_ids,
			selected_products_ids,
			selected_properties_ids,
			send,
			shop_product_id
		]
	);

	const handleOnClickExpand = useCallback(() => {
		updateAiChatStatus({
			historyOpen: false
		});
		navigate(routeNames.AI_CHAT);
	}, [updateAiChatStatus]);

	const handleOnClickMinimize = useCallback(() => {
		updateAiChatStatus({
			historyOpen: false
		});
		navigate(routeNames.DASHBOARD);
	}, [updateAiChatStatus]);

	const handleOnClickChatHistory = useCallback(() => {
		// Opening the history panel must not end the conversation.
		updateAiChatStatus({
			historyOpen: true
		});

		if (isDashboard) {
			navigate(routeNames.AI_CHAT);
		}
	}, [isDashboard, updateAiChatStatus, navigate]);

	// Track scroll position and show/hide the button
	useEffect(() => {
		const chatContainer = document.querySelector(CHAT_MESSAGES_SELECTOR);

		const handleScroll = () => {
			if (chatContainer) {
				// Show button when user has scrolled up at least 100px from bottom
				const isAtBottom = isChatScrolledToBottom(chatContainer);
				if (!isScrolling) {
					setShowScrollToBottom(!isAtBottom);
				}
				if (isAtBottom) {
					setIsScrolling(false);
				}
			}
		};

		if (chatContainer) {
			chatContainer.addEventListener("scroll", handleScroll);
			// Initial check
			handleScroll();
		}

		return () => {
			if (chatContainer) {
				chatContainer.removeEventListener("scroll", handleScroll);
			}
		};
	}, [chatMessages, isScrolling]);

	// Automatically hide the button when messages change and we're scrolling to bottom
	useEffect(() => {
		if (newPress) {
			setShowScrollToBottom(false);
		}
	}, [newPress]);

	const handleOnClickContinue = useCallback(() => {
		setIsScrolling(true);
		const chatContainer = document.querySelector(CHAT_MESSAGES_SELECTOR);
		if (chatContainer) {
			chatContainer.scrollTo({
				top: chatContainer.scrollHeight,
				behavior: "smooth"
			});
			// Hide the button after clicking
			setShowScrollToBottom(false);
		}
	}, []);

	const handleOnClickSource = useCallback(aiChatSource => {
		const { document_link, video_link, photo_link, site_link } = aiChatSource || {};

		// Handle guide links - open in new tab to preserve chat state
		if (site_link) {
			window.open(site_link, "_blank", "noopener,noreferrer");
			return;
		}

		if (document_link) {
			try {
				setFileViewer({
					type: MediaType.Document,
					src: document_link
				});
			} catch (error) {
				console.log("handleOnOpenPDF error: ", error);
			} finally {
				return;
			}
		}

		if (video_link) {
			setFileViewer({ type: MediaType.Video, src: video_link });
			return;
		}

		if (photo_link) {
			setFileViewer({ type: MediaType.Image, src: photo_link });
			return;
		}
	}, []);

	const propertyItems = useMemo(
		(): ChatFilterItem[] =>
			(loggedUserValue.user_properties || [])
				.filter(up => up.property)
				.map(up => ({ id: up.property!.id, name: up.property!.name })),
		[loggedUserValue.user_properties]
	);

	const productItems = useMemo((): ChatFilterItem[] => {
		const { installed_products = [] } = installedProductsData || {};
		const seen = new Set<string>();
		return installed_products.reduce((acc: ChatFilterItem[], ip) => {
			if (ip.product && !seen.has(ip.product_id)) {
				seen.add(ip.product_id);
				acc.push({ id: ip.product_id, name: ip.product.name });
			}
			return acc;
		}, []);
	}, [installedProductsData]);

	const handleOnSelectProperty = useCallback(
		({ key: propertyId }: { key: string }) => {
			if (selectedPropertiesFilter.includes(propertyId)) {
				setPropertyFilter(selectedPropertiesFilter.filter(id => id !== propertyId));
			} else {
				setPropertyFilter([...selectedPropertiesFilter, propertyId]);
			}
		},
		[selectedPropertiesFilter, setPropertyFilter]
	);

	const handleStartChatFromSuggestions = useCallback(
		query => {
			const { id, text } = query;
			setNewChatMessage(text);
			handleOnPressSend(text);
		},
		[handleOnPressSend]
	);

	const chatWindowHeader = useMemo(() => {
		// Doctrine only generates the chat title after reading the question, and a header that appears
		// at that moment pushes the conversation down. The question is on screen as soon as it is sent,
		// so its first characters are used as the title, in the same container, until the real one
		// arrives.
		const starterMessage = chatMessages?.find(message => !message.isSystem)?.message?.trim() || "";
		const starterTitle =
			starterMessage.length > STARTER_TITLE_MAX_LENGTH
				? `${starterMessage.slice(0, STARTER_TITLE_MAX_LENGTH).trimEnd()}…`
				: starterMessage;

		const headerTitle = chatTitle || starterTitle;

		if (!headerTitle) {
			return null;
		}

		return (
			<div className="chat-header selection">
				<span className="chat-header-doctrine-title">{headerTitle}</span>
			</div>
		);
	}, [chatMessages, chatTitle]);

	// The Brief layout is only used for the answer that opens a Brief thread. Follow-up answers are
	// rendered as normal chat messages.
	const firstSystemMessageId = useMemo(() => chatMessages?.find(msg => msg.isSystem)?.id, [chatMessages]);

	const chatWindowContent = useMemo(() => {
		if (chatMessages && chatMessages.length > 0) {
			return (
				<div className="chat-messages-container">
					{chatMessages?.map((element, index) => {
						const {
							id,
							isSystem,
							message,
							userId,
							user,
							createdAt,
							aiChatSources = [],
							isPlaceholder,
							multimodal_attachments,
							citations
						} = element as ExtendedWebAiChatMessage;

						if (isSystem) {
							// The reasoning is shown as a headline while it streams, so a copy of it inside the
							// answer is dropped instead of being read as part of it.
							const systemMessage = stripReasoning(message || "");
							const isFirstAnswer = id === firstSystemMessageId;
							const isFirstBriefMessage = isBrief && isFirstAnswer;

							// Debug logging for citations
							if (citations) {
								console.log("Maestro citations data:", {
									messageId: id,
									hasCitations: !!citations,
									chunksLength: citations?.chunks?.length,
									supportsLength: citations?.supports?.length,
									citationsType: typeof citations,
									citations: citations
								});
							}

							// Process citations if available
							const { modifiedContent, citationMap } = processContentWithCitations(
								systemMessage,
								citations ?? undefined
							);

							// The JSON is parsed from the original answer, and the neighborhood findings from the
							// version that contains the citation markers, so web results keep their tooltips.
							//
							// The decision is based on the answer and not on `is_brief`: that flag is stored on
							// the chat row, and a chat opened from a deep link (the Brief email) does not always
							// have it. Only a brief answers with the digest JSON, so an answer that parses as one
							// is rendered as one.
							const briefContent =
								isFirstAnswer && !isPlaceholder ? parseBriefAnswer(systemMessage, modifiedContent).brief : null;

							// The same applies to the answer still shown as a placeholder: once the word stream
							// closes, its text is the complete answer, so it is rendered instead of the "putting
							// it together" message, even if the mutation that saves it timed out.
							const streamedBrief =
								isPlaceholder && isFirstAnswer && isStreamComplete ? parseBriefAnswer(visibleStreamingText).brief : null;

							return (
								<div key={`chat-message-group-${id}`} className="system-chat-message-group">
									<div className="system-chat-message-container">
										<div className="system-chat-message">
											<img
												src={`${process.env.PUBLIC_URL}${theme.systemChatIcon}`}
												className="system-chat-message-img"
											/>
											<div className={`system-chat-message-text${briefContent || streamedBrief ? " brief" : ""}`}>
												{/* The reasoning is only shown while it is being written, and only as its headline. */}
												{isPlaceholder && (thoughts || isThinking) && (
													<StreamingThoughts
														thoughts={thoughts}
														hasSystemMessage={visibleStreamingText.length > 0}
														isFirstAnswer={isFirstAnswer}
													/>
												)}

												{isPlaceholder ? (
													<>
														{isLongRunning && currentStreamId && (
															<div
																className="chat-long-running-warning"
																style={{
																	display: "flex",
																	alignItems: "center",
																	gap: "8px",
																	padding: "6px 0",
																	fontSize: "0.85em",
																	color: "var(--color-text-secondary, #888)"
																}}
															>
																<span>⏳ This is taking a while...</span>
																<button
																	className="chat-stop-button"
																	onClick={handleStopStream}
																	style={{
																		padding: "2px 10px",
																		borderRadius: "4px",
																		border: "1px solid currentColor",
																		background: "transparent",
																		cursor: "pointer",
																		fontSize: "0.9em"
																	}}
																>
																	Stop
																</button>
															</div>
														)}
														{streamingError.length > 0 ? (
															<div
																className="streaming-error-message"
																style={{ color: "var(--color-error, #d9534f)", padding: "8px 0", fontSize: "0.9em" }}
															>
																⚠️ {streamingError}
															</div>
														) : visibleStreamingText.length > 0 ? (
															streamedBrief ? (
																<BriefMessage
																	brief={streamedBrief}
																	firstName={loggedUserValue.first_name}
																	frequency={briefFrequency}
																	citationMap={citationMap}
																/>
															) : isFirstBriefMessage && !isStreamComplete ? (
																// A brief is raw JSON while it streams, so a status message is shown until it closes.
																<div className="bm-preparing">Putting your brief together…</div>
															) : (
																<MarkdownWithCursor text={visibleStreamingText} />
															)
														) : null}
													</>
												) : briefContent ? (
													<BriefMessage
														brief={briefContent}
														firstName={loggedUserValue.first_name}
														frequency={briefFrequency}
														createdAt={createdAt}
														citationMap={citationMap}
													/>
												) : (
													// Show actual message content for real messages
													<ReactMarkdown
														remarkPlugins={[remarkGfm]}
														rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
														components={buildMarkdownComponents(citationMap, imgSrc =>
															setFileViewer({ type: MediaType.Image, src: imgSrc })
														)}
													>
														{modifiedContent}
													</ReactMarkdown>
												)}
												{/* Only show sources for non-placeholder messages with sources */}
												{!isPlaceholder && aiChatSources && aiChatSources.length ? (
													<>
														<div className="sources-title">{"Sources"}</div>
														{aiChatSources.map((source, sourceIndex) => {
															const primaryLinkAttributes: {
																to: string;
																onClick: any;
															} = {
																to: "",
																onClick: undefined
															};
															let sourceTypeName = "";

															if (source.product?.name) {
																sourceTypeName = source.product.name;

																if (source.installed_product?.id) {
																	primaryLinkAttributes.to = `${routeNames.INSTALLED_DEVICES}/${source.installed_product.id}`;
																} else {
																	primaryLinkAttributes.onClick = () => handleOnClickSource(source);
																}
															}
															if (source.property?.name) {
																sourceTypeName = source.property.name;
																primaryLinkAttributes.onClick = () => openModalWithProperty(source.property);
															}

															return (
																<div key={`chat-source-${id}-${sourceIndex}`} className="source-name-container">
																	{sourceTypeName && (
																		<Link className="source-name" {...primaryLinkAttributes}>
																			{sourceTypeName} -&nbsp;
																		</Link>
																	)}
																	<Link className="source-name" to="#" onClick={() => handleOnClickSource(source)}>
																		{source.attachment_name}
																	</Link>
																</div>
															);
														})}
													</>
												) : null}
											</div>
										</div>
									</div>
									{!isPlaceholder && currentThreadId && (
										<AiChatFeedback
											key={`feedback-${id}`}
											doctrineChatId={currentThreadId}
											doctrineChatMessageId={id}
											onFeedbackChange={v => setNegativeFeedbackActive(v === false)}
										/>
									)}
								</div>
							);
						} else {
							const { first_name, last_name, profile_photo } = user || {};

							// Use server attachments if available, otherwise fall back to client-side attachments
							const serverAttachments = element.multimodal_attachments || [];
							const clientAttachments = (element as ExtendedWebAiChatMessage).attachments || [];
							const rawAttachments = (serverAttachments.length > 0 ? serverAttachments : clientAttachments) as Array<{
								id: string;
								fileName: string;
								mimeType: string;
								publicUrl?: string | null;
								fileType: string;
								fileKey?: string | null;
								gsUri?: string | null;
							} | null>;
							const allAttachments = rawAttachments
								.filter((a): a is NonNullable<typeof a> => a !== null && a !== undefined)
								.map(a => ({
									...a,
									publicUrl: a.publicUrl ?? undefined,
									fileKey: a.fileKey ?? undefined,
									gsUri: a.gsUri ?? undefined
								}));

							return (
								<div key={`chat-message-${id}`} className="user-chat-message-container">
									<div className="user-chat-message">
										<div className="user-chat-message-content">
											{/* Show user's file attachments above the message text */}
											{allAttachments.length > 0 && (
												<div className="user-chat-message-attachments">
													{(() => {
														const mediaSiblings = allAttachments
															.filter(a => !!a.publicUrl)
															.map(a => ({
																src: a.publicUrl as string,
																mediaType: getMediaType(a.mimeType)
															}));
														return allAttachments.map((attachment, attachmentIndex) => (
															<FileAttachmentThumbnail
																key={`${id}-attachment-${attachmentIndex}`}
																attachment={attachment}
																size="medium"
																showFileName={false}
																siblings={mediaSiblings.length > 1 ? mediaSiblings : undefined}
																siblingIndex={mediaSiblings.findIndex(s => s.src === attachment.publicUrl)}
															/>
														));
													})()}
												</div>
											)}

											<div className="user-chat-message-text">{message.trimStart()}</div>
										</div>

										<div className="user-chat-message-img-container">
											<Avatar
												alt={`${first_name} ${last_name}`}
												src={profile_photo || `${process.env.PUBLIC_URL}/assets/images/icons/person-placeholder.svg`}
												shape={"square"}
												size={32}
												className="chat-message-avatar"
											/>
										</div>
									</div>
								</div>
							);
						}
					})}
				</div>
			);
		} else if (loadingChatMessages) {
			return (
				<div className="chat-messages-container">
					<div className="chat-loading-container">
						<Spin size="large" />
					</div>
				</div>
			);
		} else if (chatLoadError) {
			return (
				<div className="chat-messages-container">
					<div className="chat-error-container">
						<span className="chat-error-text">{chatLoadError}</span>
						<Link className="chat-error-link" to={routeNames.AI_CHAT} onClick={handleOnClickNewThread}>
							Start a new chat
						</Link>
					</div>
				</div>
			);
		} else if (chatTopic) {
			return (
				<div className="chat-messages-container">
					<div className={"chat-topic-container"}>
						<div className="chat-topic-title">Hi {loggedUserValue.first_name}!</div>

						<div className="chat-topic-text">{chatTopic}</div>
						<div className="chat-topic-text">{"How can I help you today?"}</div>
					</div>
				</div>
			);
		} else {
			// Empty state with suggestions
			const { input_suggestions = [] } = aiSuggestedQueriesData || {};

			return (
				<>
					<div className="chat-header">
						<span className="chat-header-title">{`Hi ${loggedUserValue.first_name}! How can I help you today?`}</span>
						<p className="chat-header-description">{chatHeaderDescription}</p>
						{withFilters && (
							<div className="chat-header-inputs-container">
								<ChatFilterDropdown
									items={propertyItems}
									selectedIds={selectedPropertiesFilter}
									onChangeSelectedIds={setPropertyFilter}
									allLabel="All properties"
									placeholder="Select Property"
								/>
								<ChatFilterDropdown
									items={productItems}
									selectedIds={selectedProductsFilter}
									onChangeSelectedIds={setProductsFilters}
									allLabel="All devices"
									placeholder="Select Device"
									disabled={productItems.length === 0}
								/>
							</div>
						)}
					</div>
					<div className="suggested-queries-container">
						{input_suggestions.map(query => {
							return (
								<div
									className="suggested-query"
									key={`suggested-query-${query.id}`}
									onClick={() => {
										handleStartChatFromSuggestions(query);
									}}
								>
									<img
										src={process.env.PUBLIC_URL + "/assets/images/icons/search.svg"}
										alt="search-icon"
										className="suggested-query-icon"
									/>
									<span className="suggested-query-text">{query.text}</span>
								</div>
							);
						})}
						{process.env.REACT_APP_FF_BRIEF_ENABLED === "true" && (
							<div
								className="suggested-query brief-query"
								onClick={() => handleOnPressSend(getBriefMessage(), { isBrief: true })}
							>
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/twinkle-star-purple.svg"}
									alt="brief-icon"
									className="suggested-query-icon"
								/>
								<span className="suggested-query-text">{BRIEF_BUTTON_LABEL}</span>
							</div>
						)}
					</div>
				</>
			);
		}
	}, [
		handleOnPressSend,
		handleOnClickSource,
		handleStartChatFromSuggestions,
		chatMessages,
		loggedUserValue,
		loadingChatMessages,
		isDashboard,
		withFilters,
		propertyItems,
		productItems,
		selectedPropertiesFilter,
		selectedProductsFilter,
		setPropertyFilter,
		setProductsFilters,
		aiSuggestedQueriesData,
		chatTopic,
		chatLoadError,
		handleOnClickNewThread,
		visibleStreamingText,
		thoughts,
		isThinking,
		ai_chat_user_selections,
		installedProductsData,
		isBrief,
		briefFrequency,
		isStreamComplete,
		firstSystemMessageId
	]);

	const chatInputIcons = useMemo(() => {
		const isDisabled = isUploading || !newChatMessage || isAiResponding;
		return (
			<div className="chat-icons-container">
				<img
					src={`${process.env.PUBLIC_URL}${submitIcon}`}
					className={`chat-send-icon ${isDisabled ? "disabled" : ""}`}
					onClick={() => {
						if (!isDisabled) {
							handleOnPressSend();
						}
					}}
					style={{
						opacity: isDisabled ? 0.5 : 1,
						cursor: isDisabled ? "not-allowed" : "pointer"
					}}
				/>
			</div>
		);
	}, [handleOnPressSend, isUploading, newChatMessage, submitIcon, isAiResponding]);

	// Focus the chat input when the component mounts on desktop
	useEffect(() => {
		if (chatInputRef.current && !isMobile) {
			chatInputRef.current.focus();
		}
	}, [chatInputRef]);

	// Sends `autoStartMessage` on a new thread, once the logged user data is loaded (the var starts as
	// a placeholder without id). The guard is a session var and not a ref, because the Intelligence
	// screen remounts this component on every visit.
	useEffect(() => {
		if (!autoStartMessage || chatId || !loggedUserValue?.id || adminAutoStartSentVar()) {
			return;
		}

		adminAutoStartSentVar(true);
		handleOnPressSend(autoStartMessage);
	}, [autoStartMessage, chatId, loggedUserValue?.id, handleOnPressSend]);

	return (
		<div className="chat-window">
			<div className="chat-window-top-row">
				<div className="chat-window-right-column">
					{chatWindowHeader}
					<div className="chat-window-messages-container">{chatWindowContent}</div>
				</div>
				<div className="chat-window-icons-container">
					{onBack ? (
						<div className="chat-icon-container" onClick={onBack} title="Back">
							<ArrowLeft size={18} className="chat-icon" style={{ display: "block" }} />
						</div>
					) : null}
					{isDashboard ? (
						<div className="chat-icon-container">
							<img
								src={process.env.PUBLIC_URL + "/assets/images/icons/chat-window-header-expand.svg"}
								className="chat-icon"
								onClick={handleOnClickExpand}
								title="Full Screen"
							/>
						</div>
					) : (
						<div className="chat-icon-container">
							<img
								src={process.env.PUBLIC_URL + "/assets/images/icons/minimize.svg"}
								className="chat-icon"
								onClick={handleOnClickMinimize}
								title="Minimize Chat Window"
							/>
						</div>
					)}

					<div className="chat-icon-container">
						<img
							src={process.env.PUBLIC_URL + "/assets/images/icons/edit-grey.svg"}
							className="chat-icon"
							onClick={handleOnClickNewThread}
							title="New Chat"
						/>
					</div>
					{isMobile || !historyOpen ? (
						<div className="chat-icon-container">
							<img
								src={process.env.PUBLIC_URL + "/assets/images/icons/chat-window-header-grey-paper.svg"}
								className="chat-icon"
								onClick={handleOnClickChatHistory}
								title="Chat History"
							/>
						</div>
					) : null}
				</div>
				<div className="chat-window-continue-container" style={{ display: showScrollToBottom ? "flex" : "none" }}>
					<img
						src={process.env.PUBLIC_URL + "/assets/images/icons/chat-continue-down.svg"}
						className="dashboard-chat-continue-icon"
						title="Scroll to bottom"
						onClick={handleOnClickContinue}
					/>
				</div>
			</div>

			<div
				className="dashboard-chat-input-container"
				onKeyDown={event => {
					if (event.key == "Enter" && event.shiftKey) {
						return;
					}

					if (event.key === "Enter" || event.keyCode === 13) {
						// Prevent sending if files are still uploading or the AI is still responding
						event.preventDefault();
						if (isUploading || isAiResponding) {
							return;
						}
						if (isMobile) {
							//@ts-ignore This method DOES exist
							event.target.blur(); // Remove focus to clear screen space
						}
						handleOnPressSend();
					}
				}}
			>
				{/* Hidden file input */}
				<input
					ref={fileInputRef}
					type="file"
					multiple
					style={{ display: "none" }}
					onChange={handleFileSelect}
					accept="*/*"
				/>

				<div className="chat-input-container-with-attachments">
					{/* File attachment thumbnails inside input */}
					{uploadingFiles.length > 0 && (
						<div className="chat-input-attachments">
							<FileAttachmentInput uploadingFiles={uploadingFiles} onRemoveFile={removeFile} />
						</div>
					)}

					<Input.TextArea
						className="chat-input-wrapper"
						name="newChatMessage"
						placeholder={chatMessages?.length ? "Ask a follow-up..." : "Ask anything..."}
						value={newChatMessage}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
							setNewChatMessage(event.target.value);
						}}
						autoSize={{ maxRows: 7 }}
						//@ts-ignore // This ref is valid
						ref={chatInputRef}
					/>

					<div className="chat-input-icons-row">
						<PaperclipIcon
							className={`chat-paperclip-icon ${hasFiles ? "has-files" : ""}`}
							onClick={() => fileInputRef.current?.click()}
						/>
						{chatInputIcons}
					</div>
				</div>
			</div>

			{chatType !== InputSuggestionType.INTELLIGENCE_QUERY && chatMessages?.length && !negativeFeedbackActive ? (
				<div className="dashboard-request-service-link">
					<span className="link-label">
						Require further assistance?&nbsp;
						<Link
							to={{
								pathname: routeNames.REQUEST_SERVICE
							}}
							state={{ fromAiChat: true }}
							className="inline-link"
							rel="noopener"
						>
							{"Request Service"}
						</Link>
					</span>
				</div>
			) : null}

			<MediaPlayer
				onCancel={() => setFileViewer({ type: null, src: "" })}
				src={fileViewer.src}
				open={!!fileViewer.src && fileViewer.type !== null}
				mediaType={fileViewer.type}
			/>

			<PropertyDocumentsModal
				selectedProperty={selectedProperty}
				onSelectProperty={handleOnSelectProperty}
				isOpen={propertyDocumentsModalOpen}
				setIsOpen={setPropertyDocumentsModalOpen}
			/>
		</div>
	);
};

export default ChatWindow;
